import { z } from "zod";
import { dateTimeSchema, enforceSameTenant, nonEmptyStringSchema, tenantOwnedShape, } from "./common.js";
import { connectionIdSchema, memberIdSchema, syncRunIdSchema, tenantIdSchema, } from "./ids.js";
import { memberRefSchema, connectionRefSchema } from "./refs.js";
import { providerSchema, visibilityClassSchema } from "./vocabulary.js";
export const tenantSchema = z
    .object({
    id: tenantIdSchema,
    name: nonEmptyStringSchema.max(200),
    status: z.enum(["active", "deleting", "deleted"]),
    createdAt: dateTimeSchema,
    updatedAt: dateTimeSchema,
})
    .strict();
export const memberSchema = z
    .object({
    ...tenantOwnedShape,
    id: memberIdSchema,
    authUserId: z.string().uuid(),
    role: z.enum(["owner", "member"]),
    status: z.enum(["active", "invited", "suspended", "removed"]),
    displayName: nonEmptyStringSchema.max(200),
    createdAt: dateTimeSchema,
    updatedAt: dateTimeSchema,
})
    .strict();
export const connectionStatusSchema = z.enum([
    "pending",
    "active",
    "reauthorization_required",
    "revoked",
    "failed",
]);
export const connectionSchema = z
    .object({
    ...tenantOwnedShape,
    id: connectionIdSchema,
    ownerMemberRef: memberRefSchema,
    provider: providerSchema,
    providerTenantId: nonEmptyStringSchema.nullable(),
    providerAccountId: nonEmptyStringSchema,
    accountLabel: nonEmptyStringSchema.max(320),
    defaultVisibility: visibilityClassSchema,
    status: connectionStatusSchema,
    reauthorizationReason: nonEmptyStringSchema.nullable(),
    createdAt: dateTimeSchema,
    updatedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.ownerMemberRef.tenantId, path: ["ownerMemberRef"] }], context);
    if (value.status === "reauthorization_required" &&
        value.reauthorizationReason === null) {
        context.addIssue({
            code: "custom",
            path: ["reauthorizationReason"],
            message: "A reauthorization reason is required.",
        });
    }
});
export const syncRunStatusSchema = z.enum([
    "pending",
    "running",
    "success",
    "partial",
    "failed",
    "reauthorization_required",
]);
export const syncRunSchema = z
    .object({
    ...tenantOwnedShape,
    id: syncRunIdSchema,
    connectionRef: connectionRefSchema,
    status: syncRunStatusSchema,
    startedAt: dateTimeSchema.nullable(),
    finishedAt: dateTimeSchema.nullable(),
    safeCursorBefore: nonEmptyStringSchema.nullable(),
    safeCursorAfter: nonEmptyStringSchema.nullable(),
    sourceItemsObserved: z.number().int().nonnegative(),
    sourceItemsAccounted: z.number().int().nonnegative(),
    partialCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    unsupportedCount: z.number().int().nonnegative(),
    errorCode: nonEmptyStringSchema.nullable(),
    createdAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.connectionRef.tenantId, path: ["connectionRef"] }], context);
    if (value.sourceItemsAccounted > value.sourceItemsObserved) {
        context.addIssue({
            code: "custom",
            path: ["sourceItemsAccounted"],
            message: "Accounted items cannot exceed observed items.",
        });
    }
    if (value.status === "success" &&
        value.sourceItemsAccounted !== value.sourceItemsObserved) {
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "A successful sync run must account for every observed item.",
        });
    }
});
//# sourceMappingURL=organization.js.map